"use client";

import { Eye } from "lucide-react";

import { VehicleSelectLabel } from "@/components/fleet/VehicleSelectLabel";
import { NotificationIcon } from "@/components/notifications/NotificationIcon";
import { parseDriverRef } from "@/lib/fleet/driver-ref";
import {
  notificationStyle,
  notificationAccentClass,
  type NotificationTone,
} from "@/lib/notifications/notification-style";
import { cn } from "@/lib/utils";
import type { Notification, Vehicle } from "@/types/fleet";

/** The detail reads in the event's colour; literal strings for Tailwind, as in `TONE_CLASSES`. */
const DETAIL_TONE: Record<NotificationTone, string> = {
  primary: "text-primary",
  success: "text-success",
  destructive: "text-destructive",
  warning: "text-warning",
  driver: "text-driver",
};

interface VehicleEventToastProps {
  kind: string;
  severity: Notification["type"];
  /** Event name, e.g. "Vehicle entered a no-go zone". */
  label: string;
  /** Zone name, `97 / 90 km/h`, driver — whatever the event names. */
  detail: string | null;
  vehicle: Vehicle | undefined;
  /** Shown in place of the vehicle label when the vehicle is not in the cached list. */
  fallbackVehicleName: string;
  /** The point's `driver_id`; undefined when the update did not carry it. */
  driverRef: string | undefined;
  driverNamesById: Map<string, string>;
  actionLabel: string;
  onAction: () => void;
}

/**
 * One vehicle event as a toast: the event's icon on the left, the vehicle as every other vehicle
 * popup names it (device id, plate, make and model, driver) with the event under it, and what the
 * event is about on the right.
 *
 * Rendered through `toast.custom`, so it carries the Toaster's surface styling itself.
 */
export function VehicleEventToast({
  kind,
  severity,
  label,
  detail,
  vehicle,
  fallbackVehicleName,
  driverRef,
  driverNamesById,
  actionLabel,
  onAction,
}: VehicleEventToastProps) {
  const tone = notificationStyle(kind, severity).tone;

  // The driver on the point that raised the event beats the cached list, which may be minutes old.
  let labelVehicle = vehicle;
  if (vehicle && driverRef !== undefined) {
    const ref = parseDriverRef(driverRef);
    labelVehicle = {
      ...vehicle,
      driverId: ref?.kind === "driver" ? ref.driverId : undefined,
      driverCode: ref?.kind === "code" ? ref.code : undefined,
    };
  }

  // The whole card opens the event; the button is there so people can see that it does.
  return (
    <div
      onClick={onAction}
      className={cn(
        "flex w-full cursor-pointer items-start gap-3 rounded-lg border border-border border-l-4 bg-background p-3 text-foreground shadow-lg transition-colors hover:bg-accent/40",
        notificationAccentClass(kind, severity),
      )}
    >
      <NotificationIcon kind={kind} severity={severity} />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
          {labelVehicle ? (
            <VehicleSelectLabel vehicle={labelVehicle} driverNamesById={driverNamesById} />
          ) : (
            <span className="truncate">{fallbackVehicleName}</span>
          )}
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{label}</p>
      </div>
      <div className="flex max-w-[40%] shrink-0 flex-col items-end gap-1">
        {detail && (
          <span
            className={cn("max-w-full truncate text-sm font-semibold tabular-nums", DETAIL_TONE[tone])}
            title={detail}
          >
            {detail}
          </span>
        )}
        <button
          type="button"
          onClick={(event) => {
            // The card handles the same click; without this the action would run twice.
            event.stopPropagation();
            onAction();
          }}
          className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Eye className="h-3.5 w-3.5" />
          {actionLabel}
        </button>
      </div>
    </div>
  );
}
